import React, { useState } from 'react';
import { Container, Row, Col, Accordion, Card, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
    FaQuestionCircle, 
    FaSearch, 
    FaUserPlus, 
    FaShieldAlt, 
    FaStar,
    FaPhone,
    FaEnvelope,
    FaGlobe,
    FaClock,
    FaMapMarkerAlt
} from 'react-icons/fa';
import './About.css';

const FAQ = () => {
    const [activeKey, setActiveKey] = useState('0');

    const faqData = [
        {
            category: "General",
            icon: <FaGlobe />,
            questions: [
                {
                    question: "What is Business Directory?",
                    answer: "Business Directory is a comprehensive platform that connects users with local businesses. We help you discover trusted professionals, restaurants, services, and more in your area. Our platform features verified businesses with detailed information, reviews, and contact details."
                },
                {
                    question: "How do I search for businesses?",
                    answer: "You can search for businesses using our search bar on the homepage. Enter keywords like business name, category, or location. You can also browse businesses by category or use our advanced filters to narrow down your search results."
                },
                {
                    question: "Is Business Directory free to use?",
                    answer: "Yes! Business Directory is completely free for users to browse and search for businesses. Business owners can also list their businesses for free, with optional premium features available for enhanced visibility."
                }
            ]
        },
        {
            category: "For Business Owners",
            icon: <FaUserPlus />,
            questions: [
                {
                    question: "How do I add my business to the directory?",
                    answer: "Adding your business is easy! Click on 'Add Business' in the navigation menu or footer. Fill out the business information form with details like name, address, contact information, and category. Once submitted, our team will review and approve your listing."
                },
                {
                    question: "How long does it take for my business to appear?",
                    answer: "Most business listings are reviewed and approved within 24-48 hours. We verify all information to ensure quality and accuracy. You'll receive an email notification once your listing is live."
                },
                {
                    question: "Can I edit my business information?",
                    answer: "Yes! Business owners can edit their listings at any time. Simply log in to your account and navigate to your business dashboard. You can update contact information, hours, services, and more."
                }
            ]
        },
        {
            category: "Safety & Verification",
            icon: <FaShieldAlt />,
            questions: [
                {
                    question: "How do you verify businesses?",
                    answer: "We have a comprehensive verification process that includes checking business licenses, contacting the business directly, and reviewing submitted information. All businesses are manually reviewed by our team to ensure accuracy and legitimacy."
                },
                {
                    question: "What if I find incorrect information?",
                    answer: "If you find incorrect information about a business, please contact us immediately. You can report issues through our contact form or email us directly. We take accuracy seriously and will investigate all reports."
                },
                {
                    question: "Is my personal information safe?",
                    answer: "Absolutely! We take data privacy seriously. Your personal information is encrypted and protected. We never share your contact details with businesses without your explicit permission. Read our Privacy Policy for more details."
                }
            ]
        },
        {
            category: "Reviews & Ratings",
            icon: <FaStar />,
            questions: [
                {
                    question: "Can I leave reviews for businesses?",
                    answer: "Yes! After visiting a business listed on our platform, you can leave a review and rating. Reviews help other users make informed decisions and provide valuable feedback to business owners."
                },
                {
                    question: "How are ratings calculated?",
                    answer: "Ratings are calculated based on the average of all reviews left by users. We use a weighted system that considers review quality, recency, and helpfulness to ensure fair and accurate ratings."
                },
                {
                    question: "Can businesses respond to reviews?",
                    answer: "Yes! Business owners can respond to reviews on their listings. This helps maintain transparency and allows businesses to address customer feedback directly."
                }
            ]
        },
        {
            category: "Contact & Support",
            icon: <FaPhone />,
            questions: [
                {
                    question: "How can I contact customer support?",
                    answer: "You can reach our customer support team through multiple channels: Email us at support@businessdirectory.com, call us at +1 (555) 123-4567, or use our contact form. We typically respond within 24 hours."
                },
                {
                    question: "What are your business hours?",
                    answer: "Our customer support team is available Monday through Friday, 9:00 AM to 6:00 PM EST. For urgent matters outside these hours, please email us and we'll respond as soon as possible."
                },
                {
                    question: "Do you offer technical support?",
                    answer: "Yes! We provide technical support for both users and business owners. Whether you're having trouble with the website, mobile app, or your business listing, our technical team is here to help."
                }
            ]
        }
    ];

    return (
        <div className="about-page">
            {/* Hero Section */}
            <section className="hero-section">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={8}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <div className="hero-icon">
                                    <FaQuestionCircle />
                                </div>
                                <h1 className="hero-title">Frequently Asked Questions</h1>
                                <p className="hero-subtitle">
                                    Find answers to common questions about Business Directory. 
                                    Can't find what you're looking for? Contact our support team.
                                </p>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* FAQ Content */}
            <section className="content-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <Accordion 
                                    activeKey={activeKey} 
                                    onSelect={(k) => setActiveKey(k)}
                                    className="faq-accordion"
                                >
                                    {faqData.map((category, categoryIndex) => (
                                        <Card key={categoryIndex} className="faq-category-card">
                                            <Card.Header className="faq-category-header">
                                                <div className="category-icon">
                                                    {category.icon}
                                                </div>
                                                <h3 className="category-title">{category.category}</h3>
                                            </Card.Header>
                                            <Card.Body className="faq-category-body">
                                                {category.questions.map((item, questionIndex) => (
                                                    <Accordion.Item 
                                                        key={questionIndex} 
                                                        eventKey={`${categoryIndex}-${questionIndex}`}
                                                        className="faq-item"
                                                    >
                                                        <Accordion.Header className="faq-question">
                                                            <span className="question-text">{item.question}</span>
                                                        </Accordion.Header>
                                                        <Accordion.Body className="faq-answer">
                                                            <p>{item.answer}</p>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                ))}
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </Accordion>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Contact CTA */}
            <section className="cta-section">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={8}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                            >
                                <h2>Still Have Questions?</h2>
                                <p className="cta-text">
                                    Can't find the answer you're looking for? Our support team is here to help!
                                </p>
                                <div className="contact-methods">
                                    <div className="contact-method">
                                        <FaEnvelope className="contact-icon" />
                                        <div>
                                            <h5>Email Support</h5>
                                            <p>support@businessdirectory.com</p>
                                        </div>
                                    </div>
                                    <div className="contact-method">
                                        <FaPhone className="contact-icon" />
                                        <div>
                                            <h5>Phone Support</h5>
                                            <p>+1 (555) 123-4567</p>
                                        </div>
                                    </div>
                                    <div className="contact-method">
                                        <FaClock className="contact-icon" />
                                        <div>
                                            <h5>Business Hours</h5>
                                            <p>Mon - Fri: 9:00 AM - 6:00 PM</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default FAQ; 